import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ICON_TYPE, colors, ROLES } from '../../constants';

import { selectSelectedRoleTab, setSelectedRoleTab, AppDispatch, selectSelectedRole } from 'data';

import { iconHandler, getPathByName } from '../../utils';

import { useS3Uploader } from '../useS3Uploader';
import { useTableData } from '../useTableData';

import { useAdminRoleManagementStats, usePractiseAdminRoleManagementList } from '..';
import { useRoleManagementTable } from './useRoleManagementTable';

export interface RoleManagementRow {
  sNo: number;
  fullName: string;
  gender: string;
  practiceSince: string;
  doctorId: string;
  email: string;
  statusActions: string;
  image: string;
  organisationId?: string;
}

export const useRoleManagement = (rowsPerPage: number = 10, page: number = 0) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const userRole = localStorage.getItem('userRole') || '';

  const { generateSignedUrl } = useS3Uploader();
  const selectedRole = useSelector(selectSelectedRole) || [];
  const selectedTab = useSelector(selectSelectedRoleTab) || 0;

  const {
    tableRows,
    setTableRows,
    hasMore,
    setHasMore,
    totalCount,
    setTotalCount,
    pageTimestamps,
    setPageTimestamps
  } = useTableData<RoleManagementRow>();

  // ------------------------------------------------------------------
  // Role cards config
  // ------------------------------------------------------------------

  const rolesCardListData = useMemo(() => {
    if (userRole === ROLES.SUPER_ADMIN) {
      return [
        {
          number: 0,
          title: t('practiceAdmin'),
          icon: iconHandler(ICON_TYPE.EMPATHIZE, colors.blueActiveBg, 1.2, 20),
          subTitle: t('canAddStaffMember')
        },
        {
          number: 0,
          title: t('doctor'),
          icon: iconHandler(ICON_TYPE.EMPATHIZE, colors.blueActiveBg, 1.2, 20),
          subTitle: t('provideCare')
        }
      ];
    }

    if (userRole === ROLES.PRACTISE_ADMIN) {
      return [
        {
          number: 0,
          title: t('doctor'),
          icon: iconHandler(ICON_TYPE.STETHOSCOPE, colors.blueActiveBg, 1.2, 20),
          subTitle: t('provideCare')
        },
        {
          number: 0,
          title: t('deptAdmin'),
          icon: iconHandler(ICON_TYPE.APPLEPODCAST, colors.blueActiveBg, 1.2, 20),
          subTitle: t('manageDepartments')
        },
        {
          number: 0,
          title: t('others'),
          icon: iconHandler(ICON_TYPE.NURSE, colors.blueActiveBg, 1.2, 20),
          subTitle: t('supportCare')
        }
      ];
    }

    return [];
  }, [userRole, t]);

  const [rolesCardList, setRolesCardList] = useState(rolesCardListData);

  const getSelectedRolePayload = () => {
    const selected = selectedRole?.[0]?.name;
    return [ROLES.DEPT_ADMIN, ROLES.DOCTOR, ROLES.OTHER].includes(selected) ? selected : '';
  };

  // ------------------------------------------------------------------
  // Stats & lists (TanStack Query)
  // ------------------------------------------------------------------

  const isPractiseAdmin = userRole === ROLES.PRACTISE_ADMIN;

  const { data: stats, isLoading: statsLoading } = useAdminRoleManagementStats(userRole);

  usePractiseAdminRoleManagementList(userRole, getSelectedRolePayload(), isPractiseAdmin);

  const { data, isLoading: tableLoading } = useRoleManagementTable({
    rowsPerPage,
    page,
    selectedTab: selectedTab,
    userRole,
    generateSignedUrl,
    pageTimestamps,
    setPageTimestamps
  });

  // Sync query → local table state
  useEffect(() => {
    if (!data) return;

    setTableRows(data.data ?? []);
    setHasMore(data.hasMore ?? false);
    setTotalCount(data.totalCount ?? 0);
  }, [data]);

  // ------------------------------------------------------------------
  // Update role cards from stats
  // ------------------------------------------------------------------

  useEffect(() => {
    if (!stats || !userRole) return;

    setRolesCardList((prev) =>
      prev.map((role, index) => {
        if (userRole === ROLES.PRACTISE_ADMIN) {
          const values = [
            stats.Doctors?.aggregate?.count ?? 0,
            stats.DeptAdmin?.aggregate?.count ?? 0,
            stats.Others?.aggregate?.count ?? 0
          ];
          return { ...role, number: values[index] ?? role.number };
        }

        if (userRole === ROLES.SUPER_ADMIN) {
          const values = [
            stats.roleAssignedPractiseAdminCount?.aggregate?.count ?? 0,
            stats.Doctors?.aggregate?.count ?? 0
          ];
          return { ...role, number: values[index] ?? role.number };
        }

        return role;
      })
    );
  }, [stats, userRole]);

  // ------------------------------------------------------------------
  // Actions
  // ------------------------------------------------------------------

  const handleBoxClick = (index: number) => dispatch(setSelectedRoleTab(index));

  const navigateToRoles = (practiseAdminId: string) => {
    navigate(`${getPathByName('doctorDetails')}?doctorId=${practiseAdminId}`);
  };

  // ------------------------------------------------------------------
  // Return
  // ------------------------------------------------------------------

  return {
    loading: statsLoading,
    rolesCardList,
    tableLoading,
    tableRows,
    selectedRoleTab: selectedTab,
    handleBoxClick,
    navigateToRoles,
    hasMore,
    totalCount,
    page,
    rowsPerPage
  };
};
